import { useState, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2, Sparkles, Zap, Check, Copy } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, money } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const emptyItem = () => ({ name: "", qty: 1, price: 0 });

export default function IssueReceipt() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [mode, setMode] = useState("quick");
  const [items, setItems] = useState([emptyItem()]);
  const [warrantyMonths, setWarrantyMonths] = useState(12);
  const [payment, setPayment] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);

  const total = useMemo(
    () => items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.qty) || 0), 0),
    [items]
  );

  const visibleItems = useMemo(() => items.filter((i) => i.name), [items]);

  const updateItem = (idx, key, val) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const addItem = () => setItems((a) => [...a, emptyItem()]);
  const removeItem = (idx) => setItems((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts/autofill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.items?.length) {
        setItems(data.items.map((i) => ({ name: i.name || "", qty: i.qty || 1, price: i.price || 0 })));
      }
      if (data.warranty_months) setWarrantyMonths(data.warranty_months);
      if (data.payment_method) setPayment(data.payment_method);
      setMode("quick");
      toast.success("Receipt scanned — review the details below.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (items.every((i) => !i.name)) return toast.error("Add at least one item.");
    setBusy(true);
    try {
      const { data } = await api.post("/receipts", {
        items: items.map((i) => ({ name: i.name, qty: Number(i.qty) || 1, price: Number(i.price) || 0 })),
        warranty_months: Number(warrantyMonths) || null,
        payment_method: payment || null,
        customer_email: customerEmail || null,
      });
      setIssued(data);
      qc.invalidateQueries({ queryKey: ["issued"] });
      setItems([emptyItem()]); setPayment(""); setCustomerEmail("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const claimUrl = issued ? `${window.location.origin}/claim/${issued.claim_code}` : "";
  const copyCode = () => { navigator.clipboard.writeText(issued.claim_code); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <AppLayout>
      <PageHeader eyebrow="Retailer" title="Issue a receipt"
        subtitle="Create a digital receipt. Use Quick mode to type items, or Photo autofill to scan a paper receipt." />

      {!user?.store_id && (
        <div className="mb-6 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning" data-testid="no-store-warning">
          Your account isn't linked to a store yet. Ask an admin to link you before issuing receipts.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="mb-5">
              <TabsTrigger value="quick" data-testid="tab-quick"><Zap className="mr-1.5 h-4 w-4" />Quick mode</TabsTrigger>
              <TabsTrigger value="photo" data-testid="tab-photo"><Sparkles className="mr-1.5 h-4 w-4" />Photo autofill</TabsTrigger>
            </TabsList>

            {mode === "photo" && (
              <div className="mb-6 rounded-xl border border-dashed border-border bg-card p-8 text-center">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} data-testid="photo-input" />
                <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-muted text-brand">
                  {scanning ? <Loader2 className="h-7 w-7 animate-spin" /> : <Upload className="h-7 w-7" />}
                </div>
                <p className="font-heading text-lg font-bold">{scanning ? "Scanning receipt…" : "Upload a receipt photo"}</p>
                <p className="mt-1 text-sm text-muted-foreground">Our AI extracts store, items, price and warranty automatically.</p>
                <Button onClick={() => fileRef.current?.click()} disabled={scanning} className="mt-4 bg-brand text-white hover:bg-brand-hover" data-testid="upload-photo-btn">
                  {scanning ? "Scanning…" : "Choose photo"}
                </Button>
              </div>
            )}
          </Tabs>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold">Line items</h3>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="mr-1 h-4 w-4" />Add item</Button>
            </div>
            <div className="space-y-3">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`line-item-${idx}`}>
                  <Input className="col-span-6" placeholder="Item name" value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} data-testid={`item-name-${idx}`} />
                  <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} data-testid={`item-qty-${idx}`} />
                  <Input className="col-span-3" type="number" min="0" step="0.01" placeholder="Price" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} data-testid={`item-price-${idx}`} />
                  <button className="col-span-1 grid place-items-center rounded-md text-muted-foreground hover:text-destructive" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="warranty">Warranty (months)</Label>
                <Input id="warranty" type="number" min="0" value={warrantyMonths} onChange={(e) => setWarrantyMonths(e.target.value)} className="mt-1.5" data-testid="warranty-input" />
              </div>
              <div>
                <Label htmlFor="payment">Payment method</Label>
                <Input id="payment" placeholder="e.g. Visa •••• 4242" value={payment} onChange={(e) => setPayment(e.target.value)} className="mt-1.5" data-testid="payment-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cust">Customer email (optional — auto-assigns receipt)</Label>
                <Input id="cust" type="email" placeholder="customer@example.com" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="mt-1.5" data-testid="customer-email-input" />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="sticky top-24 rounded-xl border border-border bg-card p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Summary</p>
            <div className="my-4 space-y-2">
              {visibleItems.map((i, idx) => (
                <div key={`${i.name}-${idx}`} className="flex justify-between text-sm">
                  <span className="truncate text-muted-foreground">{i.qty}× {i.name}</span>
                  <span className="font-mono tabular-nums">{money((Number(i.price) || 0) * (Number(i.qty) || 0))}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-border pt-4">
              <span className="font-heading font-bold">Total</span>
              <span className="font-heading text-2xl font-black tabular-nums">{money(total)}</span>
            </div>
            <Button onClick={submit} disabled={busy || !user?.store_id} className="mt-5 w-full bg-brand text-white hover:bg-brand-hover" data-testid="submit-receipt-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Issue receipt"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="sm:max-w-md" data-testid="issued-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Receipt issued 🎉</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="rounded-2xl border border-border bg-white p-4">
              {issued && <QRCodeCanvas value={claimUrl} size={180} fgColor="#5C24FF" data-testid="issued-qr" />}
            </div>
            <p className="text-center text-sm text-muted-foreground">Ask the customer to scan this QR, or share the claim code below.</p>
            <button onClick={copyCode} className="flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 font-mono text-lg font-bold tracking-widest" data-testid="claim-code-display">
              {issued?.claim_code} {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </button>
            <p className="text-xs text-muted-foreground">{issued?.status === "claimed" ? "Auto-assigned to the customer email." : "Waiting to be claimed."}</p>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
